/**
 * ApprovalRouting — QuTime approval-route config, co-located with Approvals
 * (owner 2026-06-27). Org-wide AND per-department scope.
 *
 * Per request-type: a short sequential chain preset (Manager / Manager→HR /
 * Manager→Admin / Manager→HR→Admin) → AttendanceRule.approval_levels. Bundle
 * orgs also get "PureOA process" → approval_process_map (ADR-031 Phase 2c).
 *
 * Scope: "Organization-wide" (global rule) or a specific department (override).
 * SAFETY: a department override row is get-or-created by the backend with
 * model-default timing fields; creating one JUST for routing would silently
 * reset that dept's clock-in/OT/geofence rules. So when a dept has no override
 * row yet, we copy the GLOBAL rule's fields into the save payload — the dept
 * row becomes behaviorally identical to global except for the routing override.
 *
 * "Reset to inherit global" (dept scope only): clears the dept's routing so it
 * follows the org-wide setting again (backend resolves dept→org on empty
 * routing). If the dept row diverges from global ONLY in routing (timing is the
 * snapshot copied at creation), the whole row is deleted so timing also inherits
 * global LIVE — no frozen snapshot left behind. If the dept has intentional
 * timing overrides, only the routing fields are cleared (timing reset is
 * DepartmentRules' domain, not this page's).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, FormControl,
  Grid, InputLabel, MenuItem, Select, Snackbar, Stack, Typography,
} from '@mui/material';
import { Cancel, Edit, RestartAlt, Save } from '@mui/icons-material';
import { ApiError, attendanceApi, processesApi, organizationApi, meApi, systemApi } from '../../../api';
import type { AttendanceRule, AttendanceRuleUpdate } from '../../../api/attendance';
import type { ProcessDefinition } from '../../../api/processes';
import { ConfirmDialog, StickyFormActions } from '../../../components/primitives';
import { RULE_SECTIONS } from '../Rules/ruleSections';
import { translateApiError } from '../../../i18n/translateApiError';
import { getProcessDefinitionDisplay } from '../../Processes/processDisplay';

const APPROVAL_REQUEST_TYPES = [
  'leave', 'overtime', 'missed_clock_in', 'business_trip', 'off_site_work', 'schedule_change',
] as const;
const APPROVAL_PRESETS: Record<string, string[]> = {
  manager: ['manager'],
  manager_hr: ['manager', 'hr'],
  manager_admin: ['manager', 'admin'],
  manager_hr_admin: ['manager', 'hr', 'admin'],
};
/**
 * WHICH PRESETS THE ORG CAN ACTUALLY STAFF (2026-09-08).
 *
 * A preset naming a level nobody holds is not REFUSED by the backend — `_resolve_level_approver('hr')`
 * resolves through `('hr_manager', 'admin', 'super_admin')`, so an admin decides and nothing is orphaned.
 * The problem is quieter: the saved setting then DESCRIBES A CHAIN THAT DOES NOT HAPPEN. The admin picked
 * HR and an admin decides, with nothing saying so (svdy-ui §19).
 *
 * Same treatment as the `process` preset directly below — offered but DISABLED, with the next step named
 * under the card. Hiding it would make the capability undiscoverable; leaving it selectable would let an
 * admin save a routing that means something else.
 */
const PRESETS_NEEDING_HR = ['manager_hr', 'manager_hr_admin'] as const;

function levelsToPreset(levels?: string[]): string {
  if (!levels || levels.length === 0) return 'manager';
  for (const [k, v] of Object.entries(APPROVAL_PRESETS)) {
    if (v.length === levels.length && v.every((x, i) => x === levels[i])) return k;
  }
  return 'manager';
}
/** Strip non-writable keys so a rule can be replayed as an update payload. */
function ruleToUpdate(rule: AttendanceRule): AttendanceRuleUpdate {
  const r = rule as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r)) {
    if (['id', 'organization_id', 'department_id', 'group_id', 'created_at'].includes(k)) continue;
    out[k] = v;
  }
  return out as AttendanceRuleUpdate;
}

const ORG = '__org__';

/** Stable key-sorted clone so two routing maps compare equal regardless of
 *  key insertion order (a dept's edited map vs the global rule's map). */
function normalizeRouting<T>(o: Record<string, T>): Record<string, T> {
  return Object.keys(o).sort().reduce((acc, k) => { acc[k] = o[k]; return acc; }, {} as Record<string, T>);
}

const ApprovalRouting: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [globalRule, setGlobalRule] = useState<AttendanceRule | null>(null);
  const [deptRules, setDeptRules] = useState<AttendanceRule[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [scope, setScope] = useState<string>(ORG); // ORG or a department id
  const [approvalLevels, setApprovalLevels] = useState<Record<string, string[]>>({});
  const [approvalProcessMap, setApprovalProcessMap] = useState<Record<string, string>>({});
  const [processDefs, setProcessDefs] = useState<ProcessDefinition[]>([]);
  // How many EMPLOYED workers hold `hr_manager`. `null` while unknown, so a preset is never greyed out
  // on a slow load — failing in the safe direction (session-discipline §12): a wrongly-enabled option
  // costs a confusing save, a wrongly-disabled one blocks a legitimate configuration.
  const [hrHolders, setHrHolders] = useState<number | null>(null);
  const [isBundle, setIsBundle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingReset, setPendingReset] = useState<{ id: string; name: string } | null>(null);
  // True when the current dept scope has NO routing override and is therefore
  // showing the live org-wide routing (inherited). Editing it creates an override.
  const [inheriting, setInheriting] = useState(false);

  /** Resolve the rule backing a scope (global, or a dept override row if any). */
  const ruleForScope = useCallback((sc: string, gr: AttendanceRule | null, drs: AttendanceRule[]) => {
    if (sc === ORG) return gr;
    return drs.find((d) => d.department_id === sc) ?? null;
  }, []);

  const loadScopeInto = useCallback((sc: string, gr: AttendanceRule | null, drs: AttendanceRule[]) => {
    const rule = ruleForScope(sc, gr, drs) as (AttendanceRule & { approval_levels?: Record<string, string[]>; approval_process_map?: Record<string, string> }) | null;
    if (sc !== ORG) {
      const ownLevels = rule?.approval_levels ?? {};
      const ownMap = rule?.approval_process_map ?? {};
      const hasOwnRouting = Object.keys(ownLevels).length > 0 || Object.keys(ownMap).length > 0;
      if (!hasOwnRouting) {
        // This department has no routing override — it inherits the org-wide
        // setting (the backend resolves dept→org on empty routing). DISPLAY the
        // LIVE global routing so the view reflects later global edits ("联动")
        // instead of empty default presets that don't track the global config.
        const g = gr as (AttendanceRule & { approval_levels?: Record<string, string[]>; approval_process_map?: Record<string, string> }) | null;
        setApprovalLevels(g?.approval_levels ?? {});
        setApprovalProcessMap(g?.approval_process_map ?? {});
        setInheriting(true);
        return;
      }
    }
    setApprovalLevels(rule?.approval_levels ?? {});
    setApprovalProcessMap(rule?.approval_process_map ?? {});
    setInheriting(false);
  }, [ruleForScope]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [gr, drs, depts] = await Promise.all([
        attendanceApi.rulesApi.getGlobal(),
        attendanceApi.rulesApi.listDepartmentRules().catch(() => [] as AttendanceRule[]),
        organizationApi.departmentsApi.list().then((rows) => rows.map((d) => ({ id: d.id, name: d.name }))).catch(() => []),
      ]);
      setGlobalRule(gr);
      setDeptRules(drs);
      setDepartments(depts);
      loadScopeInto(scope, gr, drs);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:globalRules.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, scope, loadScopeInto]);

  // Mount-only fetch; scope changes are handled by changeScope (loads from
  // already-fetched global + dept rules), so fetchAll isn't a dep here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    let cancelled = false;
    // Whether anybody holds `hr_manager`, so a preset naming HR can be greyed out rather than saved as a
    // chain that silently resolves to an admin. `worker_count` counts EMPLOYED holders (added 2026-09-08);
    // a failure leaves `null`, which keeps every preset enabled.
    systemApi.systemRolesApi.list()
      .then((roles) => {
        if (cancelled) return;
        const hr = roles.find((r) => r.code === 'hr_manager');
        setHrHolders((hr as { worker_count?: number } | undefined)?.worker_count ?? 0);
      })
      .catch(() => {});
    meApi.getDashboard()
      .then((d) => {
        const lp = (d as { licensed_products?: string[] }).licensed_products ?? [];
        const bundle = lp.includes('qutime') && lp.includes('pureoa');
        if (cancelled) return;
        setIsBundle(bundle);
        if (bundle) {
          processesApi.processDefinitionsApi.list()
            .then((defs) => {
              if (cancelled) return;
              // ROUTABLE only, and the `is_template` half is the defect the owner reported on
              // 2026-09-02 (*"你显示了所有的模版, 这是不对的"*). A seeded org carries 33
              // `is_template` definitions, ALL with `status='active'` — so filtering on status
              // alone left the whole starter catalog in the picker. A template is the thing an
              // admin COPIES from; nothing can be routed to it, and the backend now refuses it.
              setProcessDefs(defs.filter((x) => (
                (x.status ?? 'active') === 'active'
                && !(x as { is_template?: boolean }).is_template
              )));
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const changeScope = (sc: string) => {
    setScope(sc);
    setIsEditing(false);
    loadScopeInto(sc, globalRule, deptRules);
  };

  const handleCancel = () => {
    loadScopeInto(scope, globalRule, deptRules);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (scope === ORG) {
        const updated = await attendanceApi.rulesApi.updateGlobal(
          { approval_levels: approvalLevels, approval_process_map: approvalProcessMap } as AttendanceRuleUpdate,
        );
        setGlobalRule(updated);
      } else {
        const existing = deptRules.find((d) => d.department_id === scope);
        const g = globalRule as (AttendanceRule & { approval_levels?: Record<string, string[]>; approval_process_map?: Record<string, string> }) | null;
        const sameAsGlobal =
          JSON.stringify(normalizeRouting(approvalLevels)) === JSON.stringify(normalizeRouting(g?.approval_levels ?? {})) &&
          JSON.stringify(normalizeRouting(approvalProcessMap)) === JSON.stringify(normalizeRouting(g?.approval_process_map ?? {}));
        if (sameAsGlobal && !existing) {
          // Routing matches org-wide and no override row exists → keep inheriting.
          // Don't create a redundant override that would freeze a global snapshot
          // and stop tracking later global edits ("全局后改不联动").
          setInheriting(true);
          setIsEditing(false);
          setToast(t('attendance:globalRules.toastSaved'));
          return;
        }
        // If no dept override row exists yet, copy global's fields so creating
        // the row for routing doesn't reset the dept's timing rules.
        const base = existing ? {} : (globalRule ? ruleToUpdate(globalRule) : {});
        await attendanceApi.rulesApi.setDepartmentRules(scope, {
          ...base, approval_levels: approvalLevels, approval_process_map: approvalProcessMap,
        } as AttendanceRuleUpdate);
        const drs = await attendanceApi.rulesApi.listDepartmentRules().catch(() => deptRules);
        setDeptRules(drs);
        loadScopeInto(scope, globalRule, drs);
      }
      setIsEditing(false);
      setToast(t('attendance:globalRules.toastSaved'));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Does the dept's override row differ from global in any TIMING field?
   *  Routing fields (approval_levels/approval_process_map) are NOT in
   *  RULE_SECTIONS, so this measures only intentional timing customization. */
  const timingDivergesFromGlobal = useCallback((deptRule: AttendanceRule | null): boolean => {
    if (!deptRule || !globalRule) return false;
    for (const section of RULE_SECTIONS) {
      for (const field of section.fields) {
        const a = (deptRule as unknown as Record<string, unknown>)[field.key];
        const b = (globalRule as unknown as Record<string, unknown>)[field.key];
        if (JSON.stringify(a) !== JSON.stringify(b)) return true;
      }
    }
    return false;
  }, [globalRule]);

  // The "Reset to inherit global" action only makes sense when the current
  // dept scope actually carries a routing override to clear.
  const deptRoutingRule = scope === ORG ? null : (deptRules.find((d) => d.department_id === scope) ?? null);
  const deptHasRoutingOverride = (() => {
    const r = deptRoutingRule as (AttendanceRule & { approval_levels?: Record<string, unknown>; approval_process_map?: Record<string, unknown> }) | null;
    if (!r) return false;
    return Object.keys(r.approval_levels ?? {}).length > 0 || Object.keys(r.approval_process_map ?? {}).length > 0;
  })();

  /** Reset this department's approval routing so it follows the org-wide
   *  setting again (backend resolves dept→org when routing is empty). If the
   *  dept row diverges from global ONLY in routing (its timing is a global
   *  snapshot from when the row was first created for routing), delete the
   *  whole row so timing also inherits global LIVE — no frozen snapshot left
   *  behind. If the dept has intentional timing overrides, keep the row and
   *  clear only the routing fields (domain-scoped: this page owns routing,
   *  DepartmentRules owns timing). */
  const handleConfirmReset = async () => {
    if (scope === ORG) return;
    try {
      if (timingDivergesFromGlobal(deptRoutingRule)) {
        await attendanceApi.rulesApi.setDepartmentRules(scope, {
          approval_levels: null, approval_process_map: null,
        } as AttendanceRuleUpdate);
      } else {
        await attendanceApi.rulesApi.deleteDepartmentRules(scope);
      }
      const drs = await attendanceApi.rulesApi.listDepartmentRules().catch(() => deptRules);
      setDeptRules(drs);
      loadScopeInto(scope, globalRule, drs);
      setIsEditing(false);
      const name = departments.find((d) => d.id === scope)?.name ?? '';
      setToast(t('attendance:globalRules.approvalRouting.toastReset', { name }));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:deptRules.revertFailed'));
      throw e;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1.5, gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('attendance:globalRules.approvalRouting.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:globalRules.approvalRouting.description')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Scope: organization-wide or a specific department (Where-first). */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('attendance:globalRules.approvalRouting.scopeLabel')}</InputLabel>
            <Select
              value={scope}
              label={t('attendance:globalRules.approvalRouting.scopeLabel')}
              onChange={(e) => changeScope(e.target.value as string)}
            >
              <MenuItem value={ORG}>{t('attendance:globalRules.approvalRouting.scopeOrg')}</MenuItem>
              {departments.map((d) => {
                const overridden = deptRules.some((dr) => dr.department_id === d.id);
                return (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}{overridden ? ' •' : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {!isEditing && !loading && !error && deptHasRoutingOverride && (
            <Button variant="outlined" size="small" startIcon={<RestartAlt />}
              onClick={() => setPendingReset({ id: scope, name: departments.find((d) => d.id === scope)?.name ?? '' })}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.secondary', borderColor: 'divider' }}>
              {t('attendance:globalRules.approvalRouting.resetToGlobal')}
            </Button>
          )}
          {!isEditing && !loading && !error && (
            <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => setIsEditing(true)}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'primary.main', borderColor: 'primary.main', bgcolor: '#fff' }}>
              {t('attendance:rules.editRules')}
            </Button>
          )}
        </Stack>
      </Box>

      {scope !== ORG && (
        <Alert icon={false} severity="info" variant="outlined" sx={{ width: '100%', mb: 1.5, fontSize: '0.8rem' }}>
          {inheriting && !isEditing
            ? t('attendance:globalRules.approvalRouting.inheritingHint')
            : t('attendance:globalRules.approvalRouting.deptHint')}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <Card variant="outlined" sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ px: 2.5, py: 2 }}>
            <Grid container rowSpacing={1.5} columnSpacing={6}>
              {APPROVAL_REQUEST_TYPES.map((rt) => (
                <Grid item xs={12} sm={6} key={rt}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                      {t(`worker:attendanceRequests.types.${rt}`, { defaultValue: rt })}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <Select
                          value={approvalProcessMap[rt] !== undefined ? 'process' : levelsToPreset(approvalLevels[rt])}
                          disabled={!isEditing}
                          onChange={(e) => {
                            const v = e.target.value as string;
                            if (v === 'process') {
                              setApprovalProcessMap((prev) => ({ ...prev, [rt]: prev[rt] || (processDefs[0]?.id ?? '') }));
                              setApprovalLevels((prev) => { const n = { ...prev }; delete n[rt]; return n; });
                            } else {
                              setApprovalLevels((prev) => ({ ...prev, [rt]: APPROVAL_PRESETS[v] }));
                              setApprovalProcessMap((prev) => { const n = { ...prev }; delete n[rt]; return n; });
                            }
                          }}
                          sx={{ '& .MuiSelect-select': { fontWeight: !isEditing ? 600 : 400, fontSize: '0.8rem' } }}
                        >
                          {Object.keys(APPROVAL_PRESETS).map((k) => (
                            <MenuItem
                              key={k}
                              value={k}
                              disabled={hrHolders === 0 && (PRESETS_NEEDING_HR as readonly string[]).includes(k)}
                            >
                              {t(`attendance:globalRules.approvalRouting.preset.${k}`)}
                            </MenuItem>
                          ))}
                          {isBundle && (
                            // Offered even with zero routable processes, but DISABLED and with the
                            // next step named below the card. Hiding it entirely would leave the
                            // capability undiscoverable; leaving it selectable would let an admin
                            // pick a routing that saves nothing (svdy-ui §19 — a feature that
                            // cannot report its own state is indistinguishable from a broken one).
                            <MenuItem value="process" disabled={processDefs.length === 0}>
                              {t('attendance:globalRules.approvalRouting.preset.process')}
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      {approvalProcessMap[rt] !== undefined && (
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                          <Select
                            value={approvalProcessMap[rt] || ''} disabled={!isEditing} displayEmpty
                            onChange={(e) => setApprovalProcessMap((prev) => ({ ...prev, [rt]: e.target.value as string }))}
                            sx={{ '& .MuiSelect-select': { fontSize: '0.8rem' } }}
                          >
                            <MenuItem value="" disabled>{t('attendance:globalRules.approvalRouting.pickProcess')}</MenuItem>
                            {processDefs.map((d) => (
                              <MenuItem key={d.id} value={d.id}>
                                {getProcessDefinitionDisplay(t, d).name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {/* The empty state says what to do next, not just that something is missing
                (svdy-ui §7). It renders only for a BUNDLE tenant: a QuTime-only tenant is never
                offered the option, so telling them to go and build a process would be advertising
                a product they have not bought. */}
            {hrHolders === 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {t('attendance:globalRules.approvalRouting.noHrManagers')}
                </Typography>
              </Box>
            )}
            {isBundle && processDefs.length === 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {t('attendance:globalRules.approvalRouting.noProcesses')}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  {t('attendance:globalRules.approvalRouting.noProcessesNext')}
                </Typography>
                <Button
                  onClick={() => navigate('/processes/designer')}
                  size="small"
                  sx={{ mt: 1, textTransform: 'none', borderRadius: 1, px: 0 }}
                >
                  {t('attendance:globalRules.approvalRouting.openDesigner')}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Same component, same order, as every other config surface. This page was the one place
          left with Cancel before Save — the six sticky bars were unified to primary-first on the
          eye-tracking evidence (see StickyFormActions), and one page disagreeing is the drift that
          evidence was meant to end. Sticky degrades to exactly this position on a form that fits
          the viewport, which this one does, so nothing moves for the reader; it only starts
          holding position if the card ever grows past a screen. */}
      {isEditing && (
        <StickyFormActions
          primary={(
            <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} /> : <Save />}
              onClick={handleSave} disabled={saving}
              sx={{ borderRadius: 1, textTransform: 'none' }}>
              {t('attendance:globalRules.saveChanges')}
            </Button>
          )}
          secondary={(
            <Button variant="outlined" size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={saving}
              sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}>
              {t('common:actions.cancel')}
            </Button>
          )}
        />
      )}

      <ConfirmDialog
        open={!!pendingReset}
        tone="warning"
        title={t('attendance:globalRules.approvalRouting.resetTitle')}
        description={t('attendance:globalRules.approvalRouting.resetDesc')}
        subject={pendingReset && (
          <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{pendingReset.name}</Box>
        )}
        confirmLabel={t('attendance:globalRules.approvalRouting.resetConfirm')}
        confirmLoadingLabel={t('attendance:globalRules.approvalRouting.resetting')}
        cancelLabel={t('common:actions.cancel')}
        onConfirm={handleConfirmReset}
        onClose={() => setPendingReset(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ApprovalRouting;
